import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from "react-query";
import ConfirmationModal from "../../components/modals/confirmation";
import OverlayLoader from "../../components/overlayLoader";
import { deleteExpenseType, getExpenseTypes } from "../../services/expenseTypes";
import { initColumns } from "../../utils/helper";
import { onMutateSuccess, onQuerySuccess } from "../../utils/queryResponse";
import SetExpenseTypeModal from "./expenseTypeSet";
import { SkeletonRows } from "../../components/skeleton";

const PencilIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);

function getInitials(name) {
    if (!name) return '?';
    return name.trim().slice(0, 2).toUpperCase();
}

const ExpenseTypesPage = () => {
    const { t } = useTranslation();

    const [types, setTypes] = useState([])
    const [expenseTypeModal, setExpenseTypeModal] = useState(false)
    const [confirmationModal, setConfirmationModal] = useState(false)
    const [selectedType, setSelectedType] = useState()

    const queryClient = useQueryClient()

    const { data, isFetching } = useQuery(['expense-types'], getExpenseTypes)

    const { mutate, isLoading: isDeleting } = useMutation(deleteExpenseType, {
        onSuccess: onMutateSuccess((result, id) => {
            setTypes( types.filter(type => type.id !== id) )
            queryClient.invalidateQueries(['expense-types'], { refetchActive: false })
        }, t('settingsPage.expenseTypeDeleted'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setTypes(result)
        })
    }, [data])

    const handleSetExpenseTypesModal = (type) => {
        setSelectedType(type)
        setExpenseTypeModal(true)
    }

    const handleShowConfirmationModal = (type) => {
        setSelectedType(type)
        setConfirmationModal(true)
    }

    return (
        <div className="sbr">
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('settingsPage.deleteExpense')}
                message={t('settingsPage.confirmDeleteExpense')} confirmAction={() => mutate(selectedType?.id)}
            />
            <SetExpenseTypeModal display={expenseTypeModal} toggle={setExpenseTypeModal} expenseType={selectedType}/>

            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('settingsPage.expenseTypes')}</div>
                    <div className="sub">{t('settingsPage.expenseTypesDesc')}</div>
                </div>
                <div className="act">
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleSetExpenseTypesModal()}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('settingsPage.addType')}
                    </button>
                </div>
            </div>

            {/* Expense type list */}
            <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '6px 16px', position: 'relative', minHeight: '80px' }}>
                    {types.length === 0 && isFetching ? (
                        <div style={{ padding: '16px 8px' }}>
                            <SkeletonRows rows={4} widths={['16%', '40%', '20%']} />
                        </div>
                    ) : types.length === 0 ? (
                        <div className="sbr-empty">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                            </svg>
                            {t('settingsPage.noExpenseTypes')}
                        </div>
                    ) : (
                        types.map((type, idx) => (
                            <div key={type.id ?? idx} className="row-sbr">
                                <div className="a" style={{ background: 'var(--delivery-bg)', border: '1px solid #FBE3BB', color: 'var(--delivery)' }}>
                                    {getInitials(type.name)}
                                </div>
                                <div className="info">
                                    <div className="nm">{type.name}</div>
                                    {type.description && (
                                        <div className="sub">{type.description}</div>
                                    )}
                                </div>
                                <div className="rt" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button title={t('common.edit')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                        style={{ padding: '6px 8px', color: 'var(--brand)' }}
                                        onClick={() => handleSetExpenseTypesModal(type)}>
                                        <PencilIcon />
                                    </button>
                                    <button title={t('common.delete')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                        style={{ padding: '6px 8px', color: 'var(--bad)' }}
                                        onClick={() => handleShowConfirmationModal(type)}>
                                        <TrashIcon />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                    {(isFetching || isDeleting) && types.length > 0 && <OverlayLoader />}
                </div>
            </div>
        </div>
    );
};

export default ExpenseTypesPage;
